import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Typography, AppBar, Toolbar, IconButton, Dialog, Tooltip } from '@mui/material';

import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import TestIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useMassActionState } from 'src/components/massAction/MassActionStateContext';
import { useLoader } from 'src/reducers/loader';
import TestForm from 'src/components/common/TestForm';
import ActionType from 'src/enums/actionType';
import { useLocation } from 'wouter';
import {
  clearEditedData,
  useResetContentEditorFlowData,
  useSaveContent,
  useSendContent,
} from 'src/components/massAction/state/massAction';

const getContentFields = ({
  selectedResultfields,
  defaultContentFields,
  forbiddenContentFields,
}) => {
  const selectedFields = Object.values(selectedResultfields).reduce(
    (acc, currentFieldGroup) => ({
      ...acc,
      ...currentFieldGroup.reduce(
        (acc2, currentField) => ({ ...acc2, [currentField.value]: currentField.type }),
        {}
      ),
    }),
    {}
  );

  const allFields = { ...selectedFields, ...defaultContentFields };

  return Object.keys(allFields).reduce(
    (acc, fieldKey) =>
      !forbiddenContentFields.includes(fieldKey)
        ? { ...acc, [fieldKey]: allFields[fieldKey] }
        : acc,
    {}
  );
};

// FIXME #rework
const NewItems = ({ actions, classes, flowData, actionType }) => {
  const { t } = useTranslation();

  return (
    <>
      <div
        style={{
          boxShadow: 'inset -0.3px 0px 0px 0px #ffffffd5',
          padding: '0 8px',
        }}
      />
      <div
        style={{
          boxShadow: 'inset -0.3px 0px 0px 0px #ffffffd5',
          padding: '0 8px',
        }}
      >
        <Tooltip title={t('smartmessaging.massAction.navButton.saveActionMessage')}>
          <span>
            <IconButton
              aria-label="clear"
              disabled={
                !flowData.contentValidity?.isValid ||
                (!flowData.isNewContent && !flowData.hasChanges) ||
                !flowData.hasChanges
              }
              classes={{ disabled: classes.disabledBtn }}
              onClick={e => {
                e.preventDefault();
                actions.onSaveContent();
              }}
              size="large"
            >
              <SaveIcon color="secondary" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      {![
        ActionType.MEMBER_NOTIFICATION.id,
        ActionType.PUSH_XPLORACTIVE.id,
        ActionType.PROSPECTING.id,
        ActionType.SMS.id,
      ].includes(actionType) && (
        <div
          style={{
            boxShadow: 'inset -0.3px 0px 0px 0px #ffffffd5',
            padding: '0 8px',
          }}
        >
          <Tooltip title={t('smartmessaging.massAction.navButton.testContent')}>
            <span>
              <IconButton
                aria-label="clear"
                classes={{ disabled: classes.disabledBtn }}
                disabled={flowData.isNewContent || flowData.hasChanges}
                onClick={e => {
                  e.preventDefault();
                  actions.showTestForm(true);
                }}
                size="large"
              >
                <TestIcon color="secondary" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      )}
      <div
        style={{
          padding: '0 8px',
        }}
      >
        <Tooltip title={t('smartmessaging.massAction.navButton.send')}>
          <span>
            <IconButton
              aria-label="clear"
              classes={{ disabled: classes.disabledBtn }}
              disabled={
                !flowData.contentValidity?.isValid ||
                (![ActionType.MEMBER_NOTIFICATION.id, ActionType.PROSPECTING.id].includes(
                  actionType
                ) &&
                  flowData.diffusionTypeIsEmpty) ||
                flowData.isNewContent ||
                flowData.hasChanges
              }
              onClick={e => {
                e.preventDefault();
                actions.onSendContent();
              }}
              size="large"
            >
              <SendIcon color="secondary" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </>
  );
};
NewItems.propTypes = {
  actions: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  flowData: PropTypes.object.isRequired,
  actionType: PropTypes.number.isRequired,
};

const useStyles = makeStyles()(theme => ({
  disabledBtn: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.light,
    opacity: 0.7,
  },
  appBar: {
    position: 'relative',
    textAlign: 'left',
  },
  flex: {
    flex: 1,
  },
}));
function MessageActionAppBar({ actionType, actionId, isWritable }) {
  const [testFormIsVisible, showTestForm] = useState(false);
  const { classes } = useStyles();
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const setLoader = useLoader();
  const [massActionState, setMassActionState] = useMassActionState();
  const sendContent = useSendContent();
  const saveContent = useSaveContent();
  const resetContentEditorFlowData = useResetContentEditorFlowData();
  const currentAppLn = useSelector(state => state.app.currentLanguage);

  const {
    contentValidity,
    contentHasChanges: hasChanges,
    selectedDiffusionType,
    currentName,
    currentMassActionId,
    currentCampaignAction,
    defaultContentFields,
    selectedResultfields,
    forbiddenContentFields,
    forceIsNewContent,
  } = massActionState;

  const isNewContent = !currentCampaignAction?.id || forceIsNewContent;
  const contentFields = getContentFields({
    selectedResultfields,
    defaultContentFields,
    forbiddenContentFields,
  });
  const diffusionTypeIsEmpty = !selectedDiffusionType;
  async function onSaveContent() {
    setLoader(true);
    await saveContent(actionType)
      .then(() => {
        setMassActionState(state => ({ ...state, forceIsNewContent: false }));
      })
      .finally(() => {
        setLoader(false);
      });
  }

  async function onSendContent() {
    setLoader(true);
    await sendContent(actionType, () => {
      navigate(`/oneoff/${currentMassActionId}/history`);
      setMassActionState(state => ({ ...state, forceIsNewContent: false }));
      resetContentEditorFlowData();
      setMassActionState(clearEditedData);
    }).finally(() => setLoader(false));
  }

  return (
    <>
      <Dialog open={testFormIsVisible} fullWidth>
        <AppBar className={classes.appBar} color="primary">
          <Toolbar>
            <Typography variant="h5" color="inherit" style={{ flex: 1 }}>
              {t('smartmessaging.campaignEditor.testAction.popupTitle')}
            </Typography>
            <IconButton
              aria-label="clear"
              color="inherit"
              onClick={e => {
                e.preventDefault();
                showTestForm(false);
              }}
              size="large"
            >
              <CloseIcon color="secondary" />
            </IconButton>
          </Toolbar>
        </AppBar>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: '300px',
          }}
        >
          <TestForm
            forcedFields={{ field0: 'INTEGER', ...contentFields }}
            includeWizVille
            campaignActionId={(currentCampaignAction && currentCampaignAction.id) || actionId}
            actionTypeId={actionType}
            currentAppLn={currentAppLn}
          />
        </div>
      </Dialog>

      <AppBar className={classes.appBar} color="primary">
        <Toolbar>
          <div className={classes.flex}>
            <Typography variant="h5" color="inherit">
              {t(`smartmessaging.contentEditor.title.${actionType}`)}
            </Typography>
            <Typography variant="body2" color="inherit">
              {currentName}
              {!!currentMassActionId && ` (${currentMassActionId})`}
            </Typography>
          </div>
          <NewItems
            classes={classes}
            actionType={actionType}
            flowData={{
              contentValidity,
              isNewContent,
              hasChanges,
              diffusionTypeIsEmpty,
              actionId,
            }}
            actions={{ onSaveContent, onSendContent, showTestForm }}
            isWritable={isWritable}
          />
        </Toolbar>
      </AppBar>
    </>
  );
}

MessageActionAppBar.propTypes = {
  actionType: PropTypes.number.isRequired,
  actionId: PropTypes.number,
};

MessageActionAppBar.defaultProps = {
  actionId: null,
};

export default MessageActionAppBar;
